import { Badge, Button, Card, Icon } from '@salesforce/design-system-react';
import { NavLink, useNavigate } from 'react-router';

import type { LineItemEntry } from '../lineItemData';

import { IconCategory } from '../../../shared/types/Enum';
import { getContractPath, getLineItemPath } from '../lineItemData';

interface LineItemSidebarProps {
  contractId: string;
  draftItem?: LineItemEntry;
  items: LineItemEntry[];
  onDeleteItem: (itemId: string) => void;
  onDuplicateItem: (item: LineItemEntry) => void;
  selectedItemId?: string;
}

export const LineItemSidebar: React.FC<LineItemSidebarProps> = ({
  contractId,
  draftItem,
  items,
  onDeleteItem,
  onDuplicateItem,
  selectedItemId,
}) => {
  const navigate = useNavigate();
  const lineCount = items.length + (draftItem ? 1 : 0);

  return (
    <aside className="line-item-editor-sidebar">
      <Card className="line-item-editor-sidebar-shell slds-card_boundary" hasNoHeader>
        <div className="line-item-editor-sidebar-shell-content">
          <section className="line-item-editor-contract-summary">
            <div className="line-item-editor-contract-summary-header">
              <div className="line-item-editor-contract-identity">
                <Icon
                  category={IconCategory.Utility}
                  name="file"
                  size="small"
                  style={{ fill: 'var(--slds-g-color-accent-1)' }}
                />
                <div>
                  <h2 className="line-item-editor-sidebar-title slds-text-heading_small">{contractId}</h2>
                  <p className="slds-text-body_small slds-text-color_weak">Driscoll Ag Pty Ltd</p>
                </div>
              </div>
              <Button
                className="slds-button_small"
                iconCategory={IconCategory.Utility}
                iconName="edit"
                iconPosition="left"
                label="Edit Contract"
                onClick={() => {
                  void navigate(getContractPath(contractId));
                }}
                variant="neutral"
              />
            </div>
            <dl className="line-item-editor-contract-details">
              <div>
                <dt className="slds-text-body_small slds-text-color_weak">Account</dt>
                <dd className="slds-text-body_small">Driscoll Ag Pty Ltd</dd>
              </div>
              <div>
                <dt className="slds-text-body_small slds-text-color_weak">Contact</dt>
                <dd className="slds-text-body_small">Andy Nisbet</dd>
              </div>
              <div>
                <dt className="slds-text-body_small slds-text-color_weak">Marketer</dt>
                <dd className="slds-text-body_small">Cullen Minter</dd>
              </div>
            </dl>
          </section>

          <section className="line-item-editor-line-overview">
            <header className="line-item-editor-line-overview-header">
              <div className="line-item-editor-line-overview-title">
                <Icon
                  category={IconCategory.Utility}
                  name="list"
                  size="small"
                  style={{ fill: 'var(--slds-g-color-accent-1)' }}
                />
                <h2 className="line-item-editor-sidebar-title slds-text-heading_small">
                  Line Items <span className="slds-text-color_weak">({lineCount})</span>
                </h2>
              </div>
              <Button
                className="slds-button_small"
                iconCategory={IconCategory.Utility}
                iconName="add"
                iconPosition="left"
                label="Add Line"
                onClick={() => {
                  void navigate(getLineItemPath('new', contractId));
                }}
                variant="outline-brand"
              />
            </header>

            <nav aria-label="Contract line items">
              <ul className="line-item-editor-line-list">
                {draftItem ? (
                  <li className="line-item-editor-line-row line-item-editor-line-row-selected">
                    <NavLink
                      aria-current={selectedItemId === draftItem.id ? 'page' : undefined}
                      className="line-item-editor-line-link line-item-editor-line-link-draft"
                      end
                      to={getLineItemPath('new', contractId)}
                    >
                      <span className="line-item-editor-line-heading">
                        <Icon category={IconCategory.Standard} name="product" size="x-small" />
                        <span className="line-item-editor-line-name slds-text-body_regular">
                          Line {draftItem.lineNumber} · {draftItem.product}
                        </span>
                        <span className="line-item-editor-line-summary slds-text-body_small slds-text-color_weak">
                          {draftItem.priceSummary}
                        </span>
                      </span>
                      <span className="line-item-editor-line-status">
                        <Badge color="warning" content="Unsaved Changes" />
                      </span>
                    </NavLink>
                  </li>
                ) : null}
                {items.map(item => (
                  <li
                    className={`line-item-editor-line-row${
                      selectedItemId === item.id ? ' line-item-editor-line-row-selected' : ''
                    }`}
                    key={item.id}
                  >
                    <NavLink
                      aria-current={selectedItemId === item.id ? 'page' : undefined}
                      className="line-item-editor-line-link"
                      end
                      to={getLineItemPath(item.id, contractId)}
                    >
                      <span className="line-item-editor-line-heading">
                        <Icon category={IconCategory.Standard} name="product" size="x-small" />
                        <span className="line-item-editor-line-name slds-text-body_regular">
                          Line {item.lineNumber} · {item.product}
                        </span>
                        <span className="line-item-editor-line-summary slds-text-body_small slds-text-color_weak">
                          {item.priceSummary}
                        </span>
                      </span>
                    </NavLink>
                    <div
                      aria-label={`Line ${item.lineNumber} actions`}
                      className="line-item-editor-line-actions"
                      role="group"
                    >
                      <Button
                        assistiveText={{ icon: `Edit Line ${item.lineNumber}` }}
                        iconCategory={IconCategory.Utility}
                        iconName="edit"
                        onClick={() => {
                          void navigate(getLineItemPath(item.id, contractId));
                        }}
                        title={`Edit Line ${item.lineNumber}`}
                        variant="icon"
                      />
                      <Button
                        assistiveText={{ icon: `Duplicate Line ${item.lineNumber}` }}
                        iconCategory={IconCategory.Utility}
                        iconName="copy"
                        onClick={() => {
                          onDuplicateItem(item);
                        }}
                        title={`Duplicate Line ${item.lineNumber}`}
                        variant="icon"
                      />
                      <Button
                        assistiveText={{ icon: `Delete Line ${item.lineNumber}` }}
                        className="slds-button_icon-error"
                        iconCategory={IconCategory.Utility}
                        iconName="delete"
                        onClick={() => {
                          onDeleteItem(item.id);
                        }}
                        title={`Delete Line ${item.lineNumber}`}
                        variant="icon"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </nav>
            {lineCount === 0 ? (
              <div className="line-item-editor-empty-lines">
                <span aria-hidden="true" className="line-item-editor-empty-lines-icon">
                  <Icon category={IconCategory.Utility} name="list" size="small" />
                </span>
                <div>
                  <h3 className="slds-text-title_bold">No lines yet</h3>
                  <p className="slds-text-body_small slds-text-color_weak">
                    Add a line item to define its product, pricing, and shipment details.
                  </p>
                </div>
              </div>
            ) : (
              <p className="line-item-editor-line-help slds-text-body_small slds-text-color_weak">
                Select a line to edit it, or use its actions to duplicate or delete.
              </p>
            )}
          </section>
        </div>
      </Card>
    </aside>
  );
};
